import { t, type MessageKey } from "../i18n/index.ts";
import {
  alignListOrderWithSections,
  blockSectionId,
  canDeleteEditorSection,
  isPaletteSection,
  isRequiredSection,
  PALETTE_PAGE_BLOCK_KINDS,
  PALETTE_SECTION_KINDS,
  parseBlockSectionId,
  parsePhotoSectionId,
  parseSpotSectionId,
  photoSectionId,
  QUEST_SECTION_INTRO,
  QUEST_SECTION_MAP,
  QUEST_SECTION_MANNERS,
  QUEST_SECTION_PHOTO,
  QUEST_SECTION_ROUTE,
  QUEST_SECTION_SETTINGS,
  QUEST_SECTION_SPOTS,
  QUEST_SECTION_STORY,
  QUEST_SECTION_TITLE,
  reorderSectionOrder,
  shouldShowSpotSectionInEditor,
  spotSectionId,
  type PageBlockKind,
  type QuestOrderContext,
  type QuestSectionId,
} from "../../shared/quest-editor-order.ts";
import { isPageBlockKind, MAX_PAGE_BLOCKS } from "../../shared/quest-page-blocks.ts";
import { button, choiceGroup, el } from "./dom.ts";
import { CLOSE_ICON_SVG, mapControlButton } from "../map-embed-controls.ts";
import type { QuestDetail, QuestPagePhoto, QuestSpot, QuestSpotImage } from "./types.ts";
import { MAX_SPOT_PHOTOS } from "../../shared/spot-photo.ts";
import { spotTraceHasLocation, MIN_TIME_LIMIT_MIN, MAX_TIME_LIMIT_MIN } from "../../shared/route-trace.ts";
import { MAX_MISSION_DESCRIPTION, MAX_MISSION_MANNERS, MAX_MISSION_STORY, MAX_SPOT_LABEL } from "../../shared/mission-quest.ts";
import { MAX_PAGE_PHOTO_CAPTION } from "../../shared/page-photo.ts";
import { SpotLocationPicker } from "./spot-location-picker.ts";
import type { LngLat } from "../../shared/types.ts";
import { bindLiveTextControl, createDeferredCommit } from "../input/deferred-commit.ts";
import {
  blockSectionSummary,
  blockSectionTitle,
  editPageBlock,
  paletteBlockIcon,
  paletteBlockLabel,
} from "./page-block-ui.ts";

const MAX_TITLE = 80;

export type TextSectionKind = "intro" | "story" | "manners";

const TEXT_MAX: Record<TextSectionKind, number> = {
  intro: MAX_MISSION_DESCRIPTION,
  story: MAX_MISSION_STORY,
  manners: MAX_MISSION_MANNERS,
};

let activeDragPayload: string | null = null;

export interface QuestEditorColumnsHost {
  quest: QuestDetail;
  orderContext(): QuestOrderContext;
  sectionOrder: QuestSectionId[];
  setSectionOrder(order: QuestSectionId[]): void;
  selectedSection: QuestSectionId | null;
  highlightSection: QuestSectionId | null;
  selectSection(id: QuestSectionId | null): void;
  isSpotQuest(): boolean;
  adminLiveEdit: boolean;
  readyToPublish: boolean;
  onScheduleSave(): void;
  /** Reconcile dirty snapshot after non-DOM edits (map, uploads, reorder). */
  onEditorStateChanged(): void;
  /** @deprecated Use onEditorStateChanged; DOM edits reconcile via delegated listeners. */
  onSyncSaveBar(): void;
  /** Live preview + list summaries; does not rebuild expanded card fields. */
  onSyncPreview(): void;
  onRenderAll(): void;
  onToast(message: string): void;
  onPublish(): void;
  onDeleteDraft(): void;
  openRouteEditor(): void;
  addMapBlock(): void;
  hasLine(): boolean;
  mapBlock: boolean;
  settingsSummary(): string;
  openCoverPicker(): void;
  removeCover(): void;
  line(): LngLat[] | null;
  placing: "start" | "finish" | null;
  setPlacing(kind: "start" | "finish" | null): void;
  placeRow(kind: "start" | "finish"): HTMLElement;
  setStartFixed(fixed: boolean): void;
  setTimeLimit(seconds: number | null): void;
  suggestedLimit(): number;
  /** Latest spot thumbnail URL (including a pending upload not yet echoed by the server). */
  spotThumbForIndex(index: number): string | null;
  uploadSpotThumb(index: number, spot: QuestSpot, input: HTMLInputElement): Promise<void>;
  spotImagesForIndex(index: number): QuestSpotImage[];
  uploadSpotImage(index: number, input: HTMLInputElement): Promise<void>;
  removeSpotImage(index: number, slot: number): void;
  moveSpotImage(index: number, from: number, dir: -1 | 1): void;
  uploadPagePhoto(index: number, photo: QuestPagePhoto, input: HTMLInputElement): Promise<void>;
  appendPageBlockDraft(kind: PageBlockKind): void;
  uploadGalleryItem(blockIndex: number, itemIndex: number, input: HTMLInputElement): Promise<void>;
  appendSpotDraft(at?: LngLat): void;
  appendPhotoDraft(): void;
  dropSpotLocPicker(): void;
  /** Remove one spot row (pending thumbs/images and map sync). */
  removeSpotAt(index: number): void;
  /** Mount the interactive route map inside the GPS Art route card. */
  mountEditorRouteMap(container: HTMLElement): void;
}

export interface QuestEditorColumnsMount {
  editorEl: HTMLElement;
  paletteEl: HTMLElement;
  titleSave: ReturnType<typeof createDeferredCommit>;
  modalTitleInput: HTMLInputElement | null;
  setModalTitleInput(input: HTMLInputElement | null): void;
}

export function refreshSpotThumbPreview(host: QuestEditorColumnsHost, editorEl: HTMLElement, index: number): void {
  const spot = host.quest.checkpoints[index];
  const thumbUrl = host.spotThumbForIndex(index) ?? spot?.thumbnail_image ?? null;
  if (!spot && !thumbUrl) return;
  const id = spotSectionId(index);
  const li = editorEl.querySelector<HTMLElement>(`.qe-item[data-section-id="${CSS.escape(id)}"]`);
  if (!li) return;
  const rows = li.querySelectorAll(".rt-spot-thumb-row");
  if (!rows.length) return;
  for (const rowEl of rows) updateSpotThumbRow(rowEl, thumbUrl);
}

function updateSpotThumbRow(row: Element, thumbUrl: string | null): void {
  let img = row.querySelector<HTMLImageElement>(".rt-spot-thumb-preview");
  if (thumbUrl) {
    if (!img) {
      img = el("img", "rt-spot-thumb-preview") as HTMLImageElement;
      img.alt = "";
      row.append(img);
    }
    if (img.src !== thumbUrl && !img.src.endsWith(thumbUrl.split("?")[0] ?? thumbUrl)) img.src = thumbUrl;
  } else {
    img?.remove();
  }
  const btn = row.querySelector<HTMLButtonElement>(".rt-upload");
  if (btn) {
    btn.textContent = thumbUrl ? t("rt.spots.thumbReplace") : t("rt.spots.thumbUpload");
  }
}

export function mountQuestEditorColumns(host: QuestEditorColumnsHost, mount: QuestEditorColumnsMount): void {
  mount.editorEl.append(renderEditorColumn(host, mount));
  mount.paletteEl.replaceChildren(renderPalette(host));
  const list = mount.editorEl.querySelector(".qe-item-list");
  if (list instanceof HTMLElement) {
    animateAccordionExpand(list, host.selectedSection);
    scrollQuestEditorSectionIntoView(mount.editorEl, host.selectedSection);
  }
}

/** Rebuild one expanded card body without tearing down the rest of the middle column. */
export function replaceExpandedSectionEditor(
  host: QuestEditorColumnsHost,
  mount: QuestEditorColumnsMount,
  editorEl: HTMLElement,
  id: QuestSectionId,
): void {
  const li = editorEl.querySelector<HTMLElement>(`.qe-item[data-section-id="${CSS.escape(id)}"]`);
  if (!li) return;
  li.classList.add("is-expanded");
  const inner = li.querySelector(".qe-item-expand-inner");
  if (!inner) return;
  inner.replaceChildren(sectionEditor(host, mount, id));
}

export function syncQuestEditorListChrome(host: QuestEditorColumnsHost, editorEl: HTMLElement): void {
  editorEl.querySelectorAll<HTMLElement>(".qe-item").forEach((li) => {
    const id = li.dataset.sectionId as QuestSectionId;
    li.classList.toggle("is-selected", id === host.highlightSection);
    const titleEl = li.querySelector(".qe-item-title");
    if (titleEl) titleEl.textContent = sectionTitle(host, id);
    const summary = sectionSummary(host, id);
    let summaryEl = li.querySelector(".qe-item-summary");
    if (summary) {
      if (!summaryEl) {
        summaryEl = el("span", "qe-item-summary", summary);
        li.querySelector(".qe-item-body")?.append(summaryEl);
      } else summaryEl.textContent = summary;
    } else summaryEl?.remove();
  });
}

export function scrollQuestEditorSectionIntoView(container: HTMLElement, id: QuestSectionId | null): void {
  if (!id) return;
  const li = container.querySelector<HTMLElement>(`.qe-item[data-section-id="${CSS.escape(id)}"]`);
  if (!li) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  li.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" });
}

function animateAccordionExpand(list: HTMLElement, selectedId: QuestSectionId | null): void {
  if (!selectedId) return;
  const li = list.querySelector<HTMLElement>(`.qe-item[data-section-id="${CSS.escape(selectedId)}"]`);
  if (!li) return;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) {
    li.classList.add("is-expanded");
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      li.classList.add("is-expanded");
    });
  });
}

function renderEditorColumn(host: QuestEditorColumnsHost, mount: QuestEditorColumnsMount): DocumentFragment {
  const frag = document.createDocumentFragment();
  const card = el("div", "qe-guide");
  const ready = host.readyToPublish;
  card.append(
    el("p", "qe-kicker", t(host.isSpotQuest() ? "rt.spot.kicker" : "rt.kicker")),
    el("h1", "qe-heading", ready ? t("rt.headingReady") : t(host.isSpotQuest() ? "rt.spot.heading" : "rt.heading")),
    el("p", "qe-sub", ready ? t("rt.subReady") : t(host.isSpotQuest() ? "rt.spot.sub" : "rt.sub")),
  );

  const list = el("ol", "qe-item-list");
  list.dataset.dropList = "1";
  const visibleOrder = host.isSpotQuest()
    ? host.sectionOrder
    : host.sectionOrder.filter((id) => {
        const spot = parseSpotSectionId(id);
        if (spot == null) return id !== QUEST_SECTION_SETTINGS && id !== QUEST_SECTION_MAP && id !== QUEST_SECTION_SPOTS;
        return shouldShowSpotSectionInEditor(host.orderContext(), spot);
      });
  visibleOrder.forEach((id, index) => {
    const orderIndex = host.sectionOrder.indexOf(id);
    list.append(itemRow(host, mount, list, id, orderIndex >= 0 ? orderIndex : index));
  });
  bindListDrop(host, list);
  card.append(list);

  frag.append(card);
  return frag;
}

function itemRow(
  host: QuestEditorColumnsHost,
  mount: QuestEditorColumnsMount,
  list: HTMLElement,
  id: QuestSectionId,
  index: number,
): HTMLElement {
  const li = el("li", "qe-item");
  li.dataset.sectionId = id;
  const expanded = host.selectedSection === id;
  if (host.highlightSection === id) li.classList.add("is-selected");
  const card = el("div", "qe-item-card");
  const row = el("div", "qe-item-row");
  const handle = el("span", "qe-item-handle", "⋮⋮");
  handle.draggable = true;
  handle.setAttribute("aria-label", t("rt.editor.drag"));
  const collapseForDrag = () => {
    li.classList.add("is-dragging");
  };
  const restoreAfterDrag = () => {
    li.classList.remove("is-dragging");
  };
  handle.addEventListener("dragstart", (e) => {
    collapseForDrag();
    activeDragPayload = `move:${index}`;
    e.dataTransfer?.setData("text/plain", activeDragPayload);
    e.dataTransfer!.effectAllowed = "move";
  });
  handle.addEventListener("dragend", () => {
    restoreAfterDrag();
    activeDragPayload = null;
  });
  bindHandlePointerReorder(host, list, handle, index, collapseForDrag, restoreAfterDrag);
  row.append(handle);
  const body = el("div", "qe-item-body");
  body.append(el("span", "qe-item-title", sectionTitle(host, id)));
  const summary = sectionSummary(host, id);
  if (summary) body.append(el("span", "qe-item-summary", summary));
  row.append(body);
  const ctx = host.orderContext();
  const spotIdx = parseSpotSectionId(id);
  const spotKind = spotIdx != null ? host.quest.checkpoints[spotIdx]?.kind : undefined;
  const showRequiredTag = isRequiredSection(id, ctx);
  if (showRequiredTag) row.append(el("span", "rt-tag", t("rt.required")));
  if (canDeleteEditorSection(id, ctx, spotKind)) {
    const actions = el("div", "qe-item-actions");
    actions.append(
      mapControlButton("qe-item-del", t("rt.close"), CLOSE_ICON_SVG, () => {
        removeOptionalSection(host, id);
      }),
    );
    row.append(actions);
  }
  const open = () => {
    if (host.selectedSection === id) host.selectSection(null);
    else host.selectSection(id);
  };
  row.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).closest(".qe-item-handle, .qe-item-del, .qe-item-expand")) return;
    open();
  });
  row.tabIndex = 0;
  row.setAttribute("role", "button");
  row.setAttribute("aria-expanded", expanded ? "true" : "false");
  row.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  });
  card.append(row);
  if (expanded) {
    li.classList.add("is-expanded");
    const expand = el("div", "qe-item-expand");
    const inner = el("div", "qe-item-expand-inner");
    inner.addEventListener("click", (e) => e.stopPropagation());
    inner.append(sectionEditor(host, mount, id));
    expand.append(inner);
    card.append(expand);
  }
  li.append(card);
  return li;
}

function applyReorder(host: QuestEditorColumnsHost, order: QuestSectionId[]): void {
  const photos = host.quest.page_photos ?? [];
  const blocks = host.quest.page_blocks ?? [];
  const aligned = alignListOrderWithSections(order, host.quest.checkpoints, photos, blocks);
  host.quest.checkpoints = aligned.checkpoints;
  host.quest.page_photos = aligned.photos;
  host.quest.page_blocks = aligned.blocks;
  host.quest.checkpoints.forEach((cp, i) => {
    cp.seq = i;
  });
  host.setSectionOrder(aligned.order);
  host.onScheduleSave();
  host.onRenderAll();
}

function removeOptionalSection(host: QuestEditorColumnsHost, id: QuestSectionId): void {
  if (!canDeleteEditorSection(id, host.orderContext(), checkpointKindForSection(host, id))) return;
  const photo = parsePhotoSectionId(id);
  if (photo != null) {
    const photos = host.quest.page_photos ?? [];
    host.quest.page_photos = photos.filter((_, i) => i !== photo);
    let order = host.sectionOrder.filter((x) => x !== id);
    order = order.map((x) => {
      const n = parsePhotoSectionId(x);
      if (n == null) return x;
      if (n > photo) return photoSectionId(n - 1);
      return x;
    });
    applyReorder(host, order);
    return;
  }
  const block = parseBlockSectionId(id);
  if (block != null) {
    const blocks = host.quest.page_blocks ?? [];
    host.quest.page_blocks = blocks.filter((_, i) => i !== block);
    let order = host.sectionOrder.filter((x) => x !== id);
    order = order.map((x) => {
      const n = parseBlockSectionId(x);
      if (n == null) return x;
      if (n > block) return blockSectionId(n - 1);
      return x;
    });
    applyReorder(host, order);
    return;
  }
  const spot = parseSpotSectionId(id);
  if (spot != null) {
    const row = host.quest.checkpoints[spot];
    const title = row?.title?.trim() || t("rt.spots.titlePh");
    if (!window.confirm(t("rt.spots.deleteConfirm", { title }))) return;
    host.removeSpotAt(spot);
    let order = host.sectionOrder.filter((x) => x !== id);
    order = order.map((x) => {
      const n = parseSpotSectionId(x);
      if (n == null) return x;
      if (n > spot) return spotSectionId(n - 1);
      return x;
    });
    applyReorder(host, order);
    if (host.selectedSection === id || parseSpotSectionId(host.selectedSection ?? "") === spot) {
      host.selectSection(null);
    }
    return;
  }
  if (id === QUEST_SECTION_STORY) host.quest.story = null;
  else if (id === QUEST_SECTION_MANNERS) host.quest.manners = null;
  else if (id === QUEST_SECTION_INTRO) host.quest.description = "";
  host.setSectionOrder(host.sectionOrder.filter((x) => x !== id));
  if (host.selectedSection === id) host.selectSection(null);
  host.onScheduleSave();
  host.onRenderAll();
}

function checkpointKindForSection(host: QuestEditorColumnsHost, id: QuestSectionId): string | null | undefined {
  const spot = parseSpotSectionId(id);
  if (spot == null) return undefined;
  return host.quest.checkpoints[spot]?.kind;
}

function listDropIndex(list: HTMLElement, clientY: number): number {
  const items = [...list.querySelectorAll<HTMLElement>(":scope > .qe-item")];
  for (let i = 0; i < items.length; i++) {
    const rect = items[i]!.getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) return i;
  }
  return items.length;
}

function bindHandlePointerReorder(
  host: QuestEditorColumnsHost,
  list: HTMLElement,
  handle: HTMLElement,
  fromIndex: number,
  onDragStart: () => void,
  onDragEnd: () => void,
): void {
  handle.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    onDragStart();
    const pointerId = e.pointerId;
    handle.setPointerCapture(pointerId);
    activeDragPayload = `move:${fromIndex}`;
    let indicator: HTMLElement | null = null;
    const showIndicator = (beforeIndex: number) => {
      indicator?.remove();
      indicator = el("li", "qe-drop-indicator");
      const items = [...list.querySelectorAll<HTMLElement>(":scope > .qe-item")];
      if (beforeIndex >= items.length) list.append(indicator);
      else list.insertBefore(indicator, items[beforeIndex]!);
    };
    const finish = (clientY: number) => {
      indicator?.remove();
      indicator = null;
      activeDragPayload = null;
      onDragEnd();
      try {
        handle.releasePointerCapture(pointerId);
      } catch {
        /* lost capture */
      }
      const idx = listDropIndex(list, clientY);
      let to = idx;
      if (fromIndex < to) to -= 1;
      if (fromIndex !== to) applyReorder(host, reorderSectionOrder(host.sectionOrder, fromIndex, to));
    };
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      ev.preventDefault();
      showIndicator(listDropIndex(list, ev.clientY));
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      finish(ev.clientY);
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  });
}

function bindListDrop(host: QuestEditorColumnsHost, list: HTMLElement): void {
  let indicator: HTMLElement | null = null;
  const showIndicator = (beforeIndex: number) => {
    indicator?.remove();
    indicator = el("li", "qe-drop-indicator");
    const items = [...list.querySelectorAll<HTMLElement>(":scope > .qe-item")];
    if (beforeIndex >= items.length) list.append(indicator);
    else list.insertBefore(indicator, items[beforeIndex]!);
  };
  list.addEventListener("dragover", (e) => {
    e.preventDefault();
    const raw = activeDragPayload ?? "";
    if (!raw.startsWith("move:") && !raw.startsWith("palette:")) return;
    showIndicator(listDropIndex(list, e.clientY));
  });
  list.addEventListener("dragleave", (e) => {
    if (e.target === list) indicator?.remove();
  });
  list.addEventListener("drop", (e) => {
    e.preventDefault();
    indicator?.remove();
    const raw = activeDragPayload ?? e.dataTransfer?.getData("text/plain") ?? "";
    const idx = listDropIndex(list, e.clientY);
    if (raw.startsWith("palette:")) {
      const kind = raw.slice("palette:".length) as QuestSectionId;
      addPaletteSection(host, kind, idx);
      return;
    }
    if (!raw.startsWith("move:")) return;
    const from = Number(raw.slice("move:".length));
    if (!Number.isInteger(from)) return;
    let to = idx;
    if (from < to) to -= 1;
    if (from === to) return;
    applyReorder(host, reorderSectionOrder(host.sectionOrder, from, to));
  });
}

function addPaletteSection(host: QuestEditorColumnsHost, id: QuestSectionId, index: number): void {
  if (isPageBlockKind(id)) {
    if ((host.quest.page_blocks?.length ?? 0) >= MAX_PAGE_BLOCKS) {
      host.onToast(t("rt.block.limit"));
      return;
    }
    host.appendPageBlockDraft(id);
    const blockId = blockSectionId((host.quest.page_blocks?.length ?? 1) - 1);
    const order = host.sectionOrder.filter((x) => x !== blockId);
    order.splice(Math.min(index, order.length), 0, blockId);
    host.setSectionOrder(order);
    host.selectSection(blockId);
    host.onScheduleSave();
    host.onRenderAll();
    return;
  }
  if (id === QUEST_SECTION_PHOTO) {
    host.appendPhotoDraft();
    const photoId = photoSectionId((host.quest.page_photos?.length ?? 1) - 1);
    const order = host.sectionOrder.filter((x) => x !== photoId);
    order.splice(Math.min(index, order.length), 0, photoId);
    host.setSectionOrder(order);
    host.selectSection(photoId);
    host.onScheduleSave();
    host.onRenderAll();
    return;
  }
  if (id === "spot" && host.isSpotQuest()) {
    host.appendSpotDraft();
    const spotId = spotSectionId(host.quest.checkpoints.length - 1);
    const order = host.sectionOrder.filter((x) => x !== spotId);
    order.splice(Math.min(index, order.length), 0, spotId);
    host.setSectionOrder(order);
    host.selectSection(spotId);
    host.onScheduleSave();
    host.onRenderAll();
    return;
  }
  if (!isPaletteSection(id) && id !== "spot") return;
  if (host.sectionOrder.includes(id)) {
    host.selectSection(id);
    host.onRenderAll();
    return;
  }
  const order = host.sectionOrder.slice();
  order.splice(Math.min(index, order.length), 0, id);
  host.setSectionOrder(order);
  host.selectSection(id);
  host.onScheduleSave();
  host.onRenderAll();
}

function renderPalette(host: QuestEditorColumnsHost): DocumentFragment {
  const frag = document.createDocumentFragment();
  const card = el("div", "qe-palette");
  card.append(el("p", "qe-kicker qe-kicker-muted", t("rt.editor.palette")));
  const list = el("div", "qe-palette-list");
  list.append(paletteTile(host, QUEST_SECTION_PHOTO, "🖼", t("rt.editor.palettePhotoBlock")));
  for (const kind of PALETTE_PAGE_BLOCK_KINDS) {
    list.append(paletteTile(host, kind, paletteBlockIcon(kind), paletteBlockLabel(kind)));
  }
  if (host.isSpotQuest()) {
    list.append(paletteTile(host, "spot", "📍", t("rt.editor.paletteSpot")));
  }
  for (const kind of PALETTE_SECTION_KINDS) {
    list.append(paletteTile(host, kind, paletteIconForSection(kind), paletteLabel(kind)));
  }
  card.append(list);
  frag.append(card);
  return frag;
}

function paletteIconForSection(kind: QuestSectionId): string {
  if (kind === QUEST_SECTION_INTRO) return "📝";
  if (kind === QUEST_SECTION_STORY) return "📖";
  if (kind === QUEST_SECTION_MANNERS) return "🤝";
  return "•";
}

function paletteLabel(kind: QuestSectionId): string {
  if (kind === QUEST_SECTION_INTRO) return t("rt.editor.paletteText");
  return t(`rt.${kind}` as MessageKey);
}

function paletteTile(host: QuestEditorColumnsHost, kind: QuestSectionId, icon: string, label: string): HTMLElement {
  const tile = el("div", "qe-palette-tile");
  tile.draggable = true;
  tile.append(el("span", "qe-palette-icon", icon), el("span", "qe-palette-label", label));
  tile.addEventListener("dragstart", (e) => {
    activeDragPayload = `palette:${kind}`;
    e.dataTransfer?.setData("text/plain", activeDragPayload);
    e.dataTransfer!.effectAllowed = "copy";
  });
  tile.addEventListener("dragend", () => {
    activeDragPayload = null;
  });
  tile.addEventListener("click", () => {
    addPaletteSection(host, kind, host.sectionOrder.length);
  });
  return tile;
}

function sectionTitle(host: QuestEditorColumnsHost, id: QuestSectionId): string {
  const block = parseBlockSectionId(id);
  if (block != null) {
    const b = host.quest.page_blocks?.[block];
    return b ? blockSectionTitle(b) : t("rt.block.tip.title");
  }
  const photo = parsePhotoSectionId(id);
  if (photo != null) return t("rt.page.photo");
  const spot = parseSpotSectionId(id);
  if (spot != null) return t("rt.spots.typeLabel");
  if (id === QUEST_SECTION_INTRO) return t("rt.description");
  if (id === QUEST_SECTION_MAP) return t("rt.page.area");
  if (id === QUEST_SECTION_ROUTE) return t("rt.route");
  if (id === QUEST_SECTION_SETTINGS) return t("rt.settings");
  if (id === QUEST_SECTION_SPOTS) return t("rt.page.spots");
  if (id === QUEST_SECTION_TITLE) return t("rt.title");
  return t(`rt.${id}` as MessageKey);
}

function sectionSummary(host: QuestEditorColumnsHost, id: QuestSectionId): string | null {
  const block = parseBlockSectionId(id);
  if (block != null) {
    const b = host.quest.page_blocks?.[block];
    return b ? blockSectionSummary(b) : null;
  }
  const photo = parsePhotoSectionId(id);
  if (photo != null) {
    const p = host.quest.page_photos?.[photo];
    if (!p?.image) return null;
    return p.caption?.trim() || t("cover.saved");
  }
  const spot = parseSpotSectionId(id);
  if (spot != null) {
    const s = host.quest.checkpoints[spot];
    if (!s) return null;
    return s.title?.trim() || t("rt.spots.titlePh");
  }
  if (id === QUEST_SECTION_TITLE) return host.quest.title.trim() || null;
  if (id === QUEST_SECTION_ROUTE && !host.isSpotQuest()) return host.settingsSummary();
  if (id === QUEST_SECTION_INTRO) return host.quest.description?.trim() || null;
  if (id === QUEST_SECTION_STORY) return host.quest.story?.trim() || null;
  if (id === QUEST_SECTION_MANNERS) return host.quest.manners?.trim() || null;
  return null;
}

function sectionEditor(host: QuestEditorColumnsHost, mount: QuestEditorColumnsMount, id: QuestSectionId): HTMLElement {
  const blockIndex = parseBlockSectionId(id);
  if (blockIndex != null) {
    const blocks = host.quest.page_blocks ?? [];
    const block = blocks[blockIndex];
    if (!block) return el("p", "rt-note", t("rt.block.missing"));
    if (!blocks[blockIndex]) blocks[blockIndex] = block;
    host.quest.page_blocks = blocks;
    return editPageBlock(
      {
        onScheduleSave: () => host.onScheduleSave(),
        onEditorStateChanged: () => host.onEditorStateChanged(),
        onSyncPreview: () => host.onSyncPreview(),
        onRenderAll: () => host.onRenderAll(),
        onToast: (m) => host.onToast(m),
        uploadGalleryItem: (bi, ii, input) => host.uploadGalleryItem(bi, ii, input),
      },
      block,
      blockIndex,
    );
  }
  const photo = parsePhotoSectionId(id);
  if (photo != null) return photoEditor(host, photo);
  const spot = parseSpotSectionId(id);
  if (spot != null) return spotEditor(host, mount, spot);
  if (id === QUEST_SECTION_TITLE) return titleEditor(host, mount);
  if (id === QUEST_SECTION_INTRO) return textEditor(host, "intro", host.quest.description ?? "", (v) => {
    host.quest.description = v;
  });
  if (id === QUEST_SECTION_STORY) return textEditor(host, "story", host.quest.story ?? "", (v) => {
    host.quest.story = v || null;
  });
  if (id === QUEST_SECTION_MANNERS) return textEditor(host, "manners", host.quest.manners ?? "", (v) => {
    host.quest.manners = v || null;
  });
  if (id === QUEST_SECTION_ROUTE) return host.isSpotQuest() ? routeEditor(host) : routeCardEditor(host);
  if (id === QUEST_SECTION_SETTINGS) return settingsEditor(host);
  if (id === QUEST_SECTION_MAP) {
    const wrap = el("div", "qe-panel-form");
    wrap.append(el("p", "rt-note", t("rt.page.area")));
    return wrap;
  }
  if (id === QUEST_SECTION_SPOTS) return el("p", "rt-note", t("rt.page.spots"));
  return el("p", "rt-note", sectionTitle(host, id));
}

function titleEditor(host: QuestEditorColumnsHost, mount: QuestEditorColumnsMount): HTMLElement {
  const wrap = el("div", "qe-panel-form");
  wrap.append(el("h2", "rt-q", t("rt.ttl.q1")));
  let input = mount.modalTitleInput;
  if (!input) {
    input = el("input", "rt-text");
    input.maxLength = MAX_TITLE;
    mount.titleSave.bindComposition(input);
    input.addEventListener("input", () => {
      host.quest.title = input!.value;
      mount.titleSave.noteEdit();
      host.onSyncPreview();
    });
    input.addEventListener("blur", () => {
      mount.titleSave.commit();
      host.onSyncPreview();
    });
    mount.setModalTitleInput(input);
  }
  if (document.activeElement !== input) input.value = host.quest.title;
  wrap.append(input);
  appendCoverFields(host, wrap);
  return wrap;
}

function appendCoverFields(host: QuestEditorColumnsHost, wrap: HTMLElement): void {
  wrap.append(el("h2", "rt-q", t("rt.ttl.q2")));
  wrap.append(
    button(host.quest.cover_image ? t("cover.replace") : t("rt.ttl.upload"), "rt-choice rt-upload", () =>
      host.openCoverPicker(),
    ),
  );
  if (host.quest.cover_image) {
    wrap.append(button(t("cover.remove"), "rt-spot-del", () => void host.removeCover()));
  }
  wrap.append(el("p", "rt-note", t("cover.notice")));
}

function photoEditor(host: QuestEditorColumnsHost, index: number): HTMLElement {
  const photos = host.quest.page_photos ?? [];
  const photo = photos[index] ?? { image: null, caption: null };
  if (!photos[index]) photos[index] = photo;
  host.quest.page_photos = photos;
  const wrap = el("div", "qe-panel-form");
  const fileInput = el("input", "") as HTMLInputElement;
  fileInput.type = "file";
  fileInput.accept = "image/jpeg,image/webp,image/png";
  fileInput.hidden = true;
  fileInput.addEventListener("change", () => void host.uploadPagePhoto(index, photo, fileInput));
  wrap.append(
    button(photo.image ? t("cover.replace") : t("rt.ttl.upload"), "rt-choice rt-upload", () => fileInput.click()),
    fileInput,
  );
  if (photo.image) {
    const img = el("img", "qp-page-photo-preview") as HTMLImageElement;
    img.src = photo.image;
    img.alt = "";
    wrap.append(img);
  }
  const caption = el("textarea", "rt-textarea");
  caption.maxLength = MAX_PAGE_PHOTO_CAPTION;
  caption.placeholder = t("rt.page.photoCaptionPh");
  caption.value = photo.caption ?? "";
  bindLiveTextControl(caption, {
    onInput: () => {
      photo.caption = caption.value.trim() || null;
    },
    onSync: () => host.onSyncPreview(),
    scheduleSave: () => host.onScheduleSave(),
  });
  wrap.append(labeledField(t("rt.page.photoCaption"), caption));
  wrap.append(el("p", "rt-note", t("cover.notice")));
  return wrap;
}

function textEditor(
  host: QuestEditorColumnsHost,
  kind: TextSectionKind,
  value: string,
  apply: (value: string) => void,
): HTMLElement {
  const wrap = el("div", "qe-panel-form");
  const key = kind === "intro" ? "description" : kind;
  wrap.append(el("h2", "rt-q", t(`rt.${key}.q` as MessageKey)));
  const ta = el("textarea", "rt-textarea");
  ta.maxLength = TEXT_MAX[kind];
  if (kind === "intro") ta.placeholder = t("rt.description.placeholder");
  ta.value = value;
  bindLiveTextControl(ta, {
    onInput: () => apply(ta.value),
    onSync: () => host.onSyncPreview(),
    scheduleSave: () => host.onScheduleSave(),
  });
  wrap.append(ta);
  return wrap;
}

function routeEditor(host: QuestEditorColumnsHost): HTMLElement {
  const wrap = el("div", "qe-panel-form");
  wrap.append(el("h2", "rt-q", t("rt.route")));
  if (!host.hasLine() && !host.mapBlock) {
    wrap.append(button(t("rt.route.add"), "rt-add-route", () => host.addMapBlock()));
  } else {
    wrap.append(button(t("rt.page.edit"), "route-map-edit-btn", () => host.openRouteEditor()));
  }
  return wrap;
}

function routeCardEditor(host: QuestEditorColumnsHost): HTMLElement {
  const wrap = el("div", "qe-panel-form qe-route-card");
  const mapShell = el("div", "qe-route-map-shell");
  wrap.append(mapShell);
  host.mountEditorRouteMap(mapShell);
  if (host.placing) {
    const pinName = t(host.placing === "start" ? "rt.page.start" : "rt.page.finish");
    mapShell.append(el("span", "qp-map-tip", t("rt.set.tapHint", { pin: pinName })));
  }
  if (!host.hasLine() && !host.mapBlock) {
    wrap.append(button(t("rt.route.add"), "rt-add-route", () => host.addMapBlock()));
  } else {
    wrap.append(button(t("rt.page.edit"), "route-map-edit-btn", () => host.openRouteEditor()));
  }
  appendRouteSettingsFields(host, wrap);
  return wrap;
}

function appendRouteSettingsFields(host: QuestEditorColumnsHost, wrap: HTMLElement): void {
  if (!host.hasLine() && !host.mapBlock) {
    wrap.append(el("p", "rt-note", t("rt.settings.needRoute")));
    return;
  }
  wrap.append(el("h2", "rt-q", t("rt.set.q1")));
  wrap.append(
    choiceGroup([
      [t("rt.set.anywhere"), !host.quest.start_fixed, () => host.setStartFixed(false)],
      [t("rt.set.fixed"), Boolean(host.quest.start_fixed), () => host.setStartFixed(true)],
    ]),
  );
  wrap.append(el("p", "rt-note", t(host.quest.start_fixed ? "rt.set.fixedNote" : "rt.set.anywhereNote")));
  if (host.quest.start_fixed && host.hasLine()) {
    wrap.append(el("h2", "rt-q", t("rt.set.q2")), host.placeRow("start"), host.placeRow("finish"));
  }
  wrap.append(el("h2", "rt-q", t("rt.set.q3")));
  const on = host.quest.time_limit_s != null;
  wrap.append(
    choiceGroup([
      [t("rt.set.noLimit"), !on, () => host.setTimeLimit(null)],
      [t("rt.set.limit"), on, () => host.setTimeLimit(host.suggestedLimit())],
    ]),
  );
  if (on) {
    const row = el("label", "rt-limit");
    const input = el("input", "rt-limit-input") as HTMLInputElement;
    input.type = "number";
    input.min = String(MIN_TIME_LIMIT_MIN);
    input.max = String(MAX_TIME_LIMIT_MIN);
    input.inputMode = "numeric";
    input.value = String(Math.round((host.quest.time_limit_s ?? 0) / 60));
    input.addEventListener("input", () => {
      const v = Math.round(Number(input.value));
      if (!Number.isFinite(v) || v < MIN_TIME_LIMIT_MIN || v > MAX_TIME_LIMIT_MIN) return;
      host.setTimeLimit(v * 60);
    });
    row.append(el("span", "", t("rt.set.within")), input, el("span", "", t("rt.set.min")));
    wrap.append(row);
  }
}

function settingsEditor(host: QuestEditorColumnsHost): HTMLElement {
  const wrap = el("div", "qe-panel-form");
  appendRouteSettingsFields(host, wrap);
  return wrap;
}

function syncSpotFieldUi(host: QuestEditorColumnsHost): void {
  host.onSyncPreview();
}

function spotEditor(host: QuestEditorColumnsHost, _mount: QuestEditorColumnsMount, index: number): HTMLElement {
  host.dropSpotLocPicker();
  const spotAt = () => host.quest.checkpoints[index]!;
  const spot = spotAt();
  const wrap = el("div", "qe-panel-form rt-spot-form");
  const title = el("input", "rt-text");
  title.maxLength = 80;
  title.placeholder = t("rt.spots.titlePh");
  title.value = spot.title ?? "";
  bindLiveTextControl(title, {
    onInput: () => {
      spotAt().title = title.value;
    },
    onSync: () => syncSpotFieldUi(host),
    scheduleSave: () => host.onScheduleSave(),
  });
  const desc = el("textarea", "rt-textarea");
  desc.maxLength = MAX_SPOT_LABEL;
  desc.placeholder = t("rt.spots.descPh");
  desc.value = spot.label ?? "";
  bindLiveTextControl(desc, {
    onInput: () => {
      spotAt().label = desc.value.trim() || null;
    },
    onSync: () => syncSpotFieldUi(host),
    scheduleSave: () => host.onScheduleSave(),
  });
  const fileInput = el("input", "") as HTMLInputElement;
  fileInput.type = "file";
  fileInput.accept = "image/jpeg,image/webp,image/png";
  fileInput.hidden = true;
  fileInput.addEventListener("change", () => void host.uploadSpotThumb(index, spotAt(), fileInput));
  const thumbUrl = host.spotThumbForIndex(index) ?? spot.thumbnail_image ?? null;
  const thumbBtn = button(
    thumbUrl ? t("rt.spots.thumbReplace") : t("rt.spots.thumbUpload"),
    "rt-choice rt-upload",
    () => fileInput.click(),
  );
  const locHost = el("div", "rt-spot-loc-host");
  const initial: LngLat = spotTraceHasLocation(spot) ? [spot.lng, spot.lat] : [host.quest.center_lng, host.quest.center_lat];
  const thumbWrap = el("div", "rt-spot-thumb-row");
  thumbWrap.append(thumbBtn, fileInput);
  if (thumbUrl) {
    const img = el("img", "rt-spot-thumb-preview") as HTMLImageElement;
    img.src = thumbUrl;
    img.alt = "";
    thumbWrap.append(img);
  }
  const picker = new SpotLocationPicker({
    container: locHost,
    initial,
    onConfirm: (point) => {
      const cp = spotAt();
      cp.lng = point[0];
      cp.lat = point[1];
      host.onEditorStateChanged();
      host.onScheduleSave();
      host.onRenderAll();
    },
    onToast: (message) => host.onToast(message),
  });
  wrap.append(
    labeledField(t("quest.spot.title"), title),
    labeledField(t("rt.description"), desc),
    labeledField(t("rt.spots.thumb"), thumbWrap),
    spotImagesEditor(host, index),
    labeledField(t("rt.spots.location"), locHost),
  );
  void picker;
  return wrap;
}

function spotImagesEditor(host: QuestEditorColumnsHost, index: number): HTMLElement {
  const spotAt = () => host.quest.checkpoints[index]!;
  const items = host.spotImagesForIndex(index);
  const spot = spotAt();
  if (!spot.images) spot.images = [];
  spot.images = items.map((it, i) => ({
    image: it.image,
    caption: spot.images?.[i]?.caption ?? it.caption ?? null,
  }));
  const wrap = el("div", "rt-spot-images-editor");
  const list = el("div", "rt-spot-images-list");
  spot.images.forEach((item, slot) => {
    const row = el("div", "rt-spot-images-item");
    const img = el("img", "rt-spot-images-preview") as HTMLImageElement;
    img.src = item.image;
    img.alt = item.caption?.trim() ?? "";
    row.append(img);
    const meta = el("div", "rt-spot-images-meta");
    const caption = el("textarea", "rt-textarea rt-spot-image-caption");
    caption.maxLength = MAX_PAGE_PHOTO_CAPTION;
    caption.placeholder = t("rt.spots.imageCaptionPh");
    caption.rows = 2;
    caption.value = item.caption ?? "";
    bindLiveTextControl(caption, {
      onInput: () => {
        item.caption = caption.value.trim() || null;
      },
      onSync: () => syncSpotFieldUi(host),
      scheduleSave: () => host.onScheduleSave(),
    });
    meta.append(caption);
    const actions = el("div", "rt-spot-images-actions");
    if (slot > 0) {
      actions.append(
        button(t("rt.spots.imagesMoveUp"), "rt-choice", () => host.moveSpotImage(index, slot, -1)),
      );
    }
    if (slot < spot.images!.length - 1) {
      actions.append(
        button(t("rt.spots.imagesMoveDown"), "rt-choice", () => host.moveSpotImage(index, slot, 1)),
      );
    }
    actions.append(
      button(t("rt.spots.imagesRemove"), "rt-spot-del", () => host.removeSpotImage(index, slot)),
    );
    meta.append(actions);
    row.append(meta);
    list.append(row);
  });
  wrap.append(list);
  if (spot.images.length < MAX_SPOT_PHOTOS) {
    const fileInput = el("input", "") as HTMLInputElement;
    fileInput.type = "file";
    fileInput.accept = "image/jpeg,image/webp,image/png";
    fileInput.hidden = true;
    fileInput.addEventListener("change", () => void host.uploadSpotImage(index, fileInput));
    wrap.append(
      button(t("rt.spots.imagesAdd"), "rt-choice rt-upload", () => fileInput.click()),
      fileInput,
    );
  }
  return labeledField(t("rt.spots.images"), wrap);
}

function labeledField(label: string, control: HTMLElement): HTMLElement {
  const row = el("label", "rt-field");
  row.append(el("span", "rt-field-label", label), control);
  return row;
}
